import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

/** How close to the end, in points, still counts as reading the latest message. */
const FOLLOW_END_DISTANCE = 48;

/** How far from the end, in points, a list that means to sit at the end may land. */
const AT_END_TOLERANCE = 1;

function getDistanceFromEnd({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>): number {
  const { contentOffset, contentSize, layoutMeasurement, contentInset } = nativeEvent;

  return (
    contentSize.height + (contentInset?.bottom ?? 0) - contentOffset.y - layoutMeasurement.height
  );
}

type FollowListEndOptions = {
  /** False where the list follows its end some other way, as an inverted list does. */
  isEnabled?: boolean;
  /**
   * Identifies the thread on screen. A new key is a new thread, which opens on its newest
   * message without motion whatever the reader was doing in the last one.
   */
  listKey?: string;
};

type ScrollableList = {
  scrollToEnd: (params?: { animated?: boolean }) => void;
  scrollToOffset?: (params: { offset: number; animated?: boolean }) => void;
};

type FollowListEnd = {
  /** Scrolls to the newest message and resumes following it, whatever the reader was doing. */
  scrollToLatest: () => void;
  /**
   * Scrolls to the oldest message and stops following the end, as a reader's own scroll would.
   * Without that, the list reads the trip up as drift and pins itself straight back.
   */
  scrollToStart: () => void;
  /** Re-pins to the end if the reader is following it, such as after an inset changes. */
  followEnd: (animated: boolean) => void;
  listHandlers: {
    onScrollBeginDrag: () => void;
    onScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onMomentumScrollEnd: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onContentSizeChange: () => void;
    onLayout: () => void;
  };
};

/**
 * Keeps a chat list that is not inverted on its newest message, the way an inverted list
 * stays there for free.
 *
 * It follows the end only while the reader is there: new messages, a growing composer and an
 * opening keyboard keep the latest message in view, but a reader scrolled back through history
 * stays where they are. Only the reader's own scrolling decides that. iOS also reports the
 * list's own scrolls to the end as a momentum end, at a position the next row may already have
 * moved past, so a momentum end counts only when a drag started it.
 *
 * A momentum end the list caused itself re-pins only when it landed short of the end. iOS
 * reports a momentum end for every scroll to the end, including one that did not move, so
 * re-pinning unconditionally answers each with another and never stops, and a reader's drag
 * cannot get a hold on a list that is scrolling to its end thousands of times a second.
 *
 * A drag released without a fling has no momentum end to close it, so the drag ends there.
 * Left open, the list's own next scroll to the end would be taken for the reader settling
 * short of a new row, and the list would stop following.
 *
 * Older pages arriving at the top are not this hook's concern: the list keeps its place for
 * those through `maintainVisibleContentPosition`.
 */
export function useFollowListEnd(
  listRef: RefObject<ScrollableList | null>,
  itemCount: number,
  { isEnabled = true, listKey }: FollowListEndOptions = {}
): FollowListEnd {
  const isFollowingEnd = useRef(true);

  const isDragging = useRef(false);

  const hasShownContent = useRef(false);

  const lastItemCount = useRef(itemCount);

  const isItemsChangePending = useRef(false);

  const lastListKey = useRef(listKey);

  const followEnd = useCallback(
    (animated: boolean) => {
      if (isEnabled && isFollowingEnd.current) {
        listRef.current?.scrollToEnd({ animated });
      }
    },
    [isEnabled, listRef]
  );

  const scrollToLatest = useCallback(() => {
    isFollowingEnd.current = true;

    if (isEnabled) {
      listRef.current?.scrollToEnd({ animated: true });
    }
  }, [isEnabled, listRef]);

  const scrollToStart = useCallback(() => {
    isFollowingEnd.current = false;

    listRef.current?.scrollToOffset?.({ offset: 0, animated: true });
  }, [listRef]);

  const settleAfterDrag = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    isFollowingEnd.current = getDistanceFromEnd(event) < FOLLOW_END_DISTANCE;
  }, []);

  const listHandlers = useMemo(
    () => ({
      onScrollBeginDrag: () => {
        isDragging.current = true;

        isFollowingEnd.current = false;
      },
      onScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        settleAfterDrag(event);

        isDragging.current = Math.abs(event.nativeEvent.velocity?.y ?? 0) > 0;
      },
      onMomentumScrollEnd: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if (isDragging.current) {
          isDragging.current = false;

          settleAfterDrag(event);

          return;
        }

        if (getDistanceFromEnd(event) > AT_END_TOLERANCE) {
          followEnd(false);
        }
      },
      onContentSizeChange: () => {
        hasShownContent.current = true;

        if (!isItemsChangePending.current) {
          followEnd(false);
        }
      },
      onLayout: () => followEnd(false),
    }),
    [followEnd, settleAfterDrag]
  );

  /*
   * Messages arriving re-pin on the next frame, once they are laid out, rather than waiting
   * for the content to change size. A thread shorter than the list fills it whatever it holds,
   * so its content size never changes, and a growing list header had already scrolled it off
   * the end. A new message slides in; the first page lands without motion.
   */
  useEffect(() => {
    if (lastListKey.current !== listKey) {
      lastListKey.current = listKey;

      isFollowingEnd.current = true;

      isDragging.current = false;

      hasShownContent.current = false;

      lastItemCount.current = -1;
    }

    if (lastItemCount.current === itemCount) {
      return undefined;
    }

    const isNewMessage = hasShownContent.current && lastItemCount.current > 0;

    lastItemCount.current = itemCount;

    isItemsChangePending.current = true;

    const frame = requestAnimationFrame(() => {
      isItemsChangePending.current = false;

      followEnd(isNewMessage);
    });

    return () => {
      cancelAnimationFrame(frame);

      isItemsChangePending.current = false;
    };
  }, [followEnd, itemCount, listKey]);

  return { scrollToLatest, scrollToStart, followEnd, listHandlers };
}
